<template>
  <div :class="$style['hypo-fee-trading-commission']">
    <!-- Add Button -->
    <div v-if="show && !open" @click="$emit('toggle-trading-commission', true)">
      <mds-button variation="flat" icon="plus" type="button" size="small">
        {{ translate('addTradingCommissions') }}
      </mds-button>
    </div>
    <mds-layout-grid v-if="open" :class="$style['content']">
      <mds-row :class="$style['title']">
        <mds-col :class="$style['title-text']"> {{ translate('tradingCommissions') }} </mds-col>
        <mds-col :class="$style['title-button']">
          <mds-button
            variation="flat"
            icon="remove"
            type="button"
            size="small"
            @click="handleRemoveButtonClick"
          >
            {{ translate('remove') }}
          </mds-button>
        </mds-col>
      </mds-row>
      <mds-row>
        <mds-col :cols="12">
          {{ translate('tradingCommissionsExplaination') }}
        </mds-col>
      </mds-row>
      <mds-row>
        <mds-col :cols="12">
          <mds-form size="small">
            <mds-fieldset size="small" variation="radio-group">
              <mds-radio-button
                v-for="item in tradingCommissionTypeOptions"
                :key="item.labelKey"
                v-model="tradingCommissionType"
                name="tradingCommission"
                size="small"
                :value="item.value"
              >
                {{ translate(item.labelKey) }}
              </mds-radio-button>

              <mds-row v-if="tradingCommissionType !== 3" :class="$style['input-row']">
                <mds-col :cols="9">
                  <mds-input
                    size="small"
                    hidden-label
                    label="number-input"
                    :value="formatter.number(tradingCommissionAmount, 'integer')"
                    @change="handleAmountChange"
                  >
                  </mds-input>
                </mds-col>
                <mds-col :cols="3">
                  <mds-button-group
                    size="small"
                    :content="amountTypeSwitchOptions"
                    @mds-button-group-item-active="handleamountTypeChange"
                  ></mds-button-group>
                </mds-col>
              </mds-row>
              <mds-radio-button
                v-else
                v-model="tradingCommissionType"
                name="tradingCommission"
                size="small"
                :value="3"
              >
                {{ variesLabel }}
              </mds-radio-button>
            </mds-fieldset>
          </mds-form>
        </mds-col>
      </mds-row>
    </mds-layout-grid>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import MdsButtonGroup from '@mds/button-group';
import MdsInput from '@mds/input';
import { MdsButton } from '@mds/button';
import MdsForm from '@mds/form';
import MdsFieldset from '@mds/fieldset';
import MdsRadioButton from '@mds/radio-button';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { parseNumber, isInvalidAmount, isInvalidPercentage } from '../../utils/utils';
import { TRADING_COMISSION_TYPES } from '../../utils/constants';
import { tradingCommissionTypeOptions } from '../../utils/input-options';

const { BUY_AND_SELL, VAH, AMOUNT, PERCENT } = TRADING_COMISSION_TYPES;

export default {
  name: 'trading-commission',
  components: {
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsButtonGroup,
    MdsInput,
    MdsButton,
    MdsForm,
    MdsFieldset,
    MdsRadioButton,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    open: {
      type: Boolean,
      default: false,
    },
    secWithTC: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      tradingCommissionType: BUY_AND_SELL,
      tradingCommissionAmount: 0,
      amountType: AMOUNT,
    };
  },
  computed: {
    show() {
      return this.secWithTC.length > 0 && this.secWithTC.every(sec => sec.tradingCommission);
    },
    clonedCoreData() {
      const { tradingCommissionType, tradingCommissionAmount, amountType } = this.$data;
      return cloneDeep({ tradingCommissionType, tradingCommissionAmount, amountType });
    },
    tradingCommissionTypeOptions() {
      return tradingCommissionTypeOptions;
    },
    amountTypeSwitchOptions() {
      return [
        {
          text: '%',
          value: PERCENT,
          active: this.amountType === PERCENT,
        },
        {
          text: this.currencySymbol,
          value: AMOUNT,
          active: this.amountType === AMOUNT,
        },
      ];
    },
    variesLabel() {
      const labelKey = this.settings.isActualWorkflow
        ? 'variesAcrossInvestments'
        : 'variesAcrossHoldings';
      return this.translate(labelKey);
    },
  },
  watch: {
    // secWithTC has already been filtered outside, so no need to check whether sec type is one of ST, FC, FE
    secWithTC: {
      handler(secList) {
        this.setDataAccordingSecWithTC(secList);
      },
      deep: true,
    },
    clonedCoreData: {
      handler() {
        if (this.tradingCommissionType !== VAH) {
          this.emitData();
        }
      },
      deep: true,
    },
  },
  created() {
    this.setDataAccordingSecWithTC(this.secWithTC);
  },
  methods: {
    handleamountTypeChange(e) {
      this.amountType = Number(e.target.value);
      this.tradingCommissionAmount = 0;
    },
    handleAmountChange(e) {
      const amount = parseNumber(e.target.value);
      const invalidPercent = this.amountType === PERCENT && isInvalidPercentage(amount);
      const numberUnchanged = amount === this.tradingCommissionAmount;
      if (isInvalidAmount(amount) || invalidPercent || numberUnchanged) {
        e.target.value = this.formatter.number(this.tradingCommissionAmount, 'integer');
      } else {
        this.tradingCommissionAmount = amount;
      }
    },
    handleRemoveButtonClick() {
      this.$emit('toggle-trading-commission', false);
      this.setDataAccordingSecWithTC(this.secWithTC);
    },
    emitData() {
      this.$emit('trading-commission-change', this.clonedCoreData);
    },
    setDataAccordingSecWithTC(secList) {
      if (secList.length === 0) {
        return;
      }
      let flagSec = null;
      for (let i = 0; i < secList.length; i += 1) {
        const sec = secList[i];
        if (!flagSec) {
          flagSec = sec;
        }
        // case: Varies Across Holdings
        if (!isEqual(flagSec.tradingCommission, sec.tradingCommission)) {
          this.tradingCommissionType = VAH;
          this.tradingCommissionAmount = 0;
          this.amountType = AMOUNT;
          return;
        }
      }
      const sample = secList[0].tradingCommission || {};
      this.tradingCommissionType = sample.tradingCommissionType;
      this.tradingCommissionAmount = sample.tradingCommissionAmount;
      this.amountType = sample.amountType;
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/typography';

.hypo-fee-trading-commission {
  @include mds-body-text-s();
  margin-top: $mds-space-1-and-a-half-x;
  max-width: 360px;
  .title {
    &-text {
      margin-top: 3px;
      @include mds-body-text-s($bold: true);
    }
    &-button {
      display: flex;
      justify-content: flex-end;
    }
  }
  .mds-form {
    margin: $mds-space-1-x 0;
  }

  .input-row {
    margin-top: $mds-space-three-quarter-x;
    margin-bottom: $mds-space-1-x;
  }

  .mds-input {
    margin-left: $mds-space-2-and-a-half-x;
  }
}
</style>
